import { emptySplitApi as api } from "./emptyApi";
export const addTagTypes = [
  "System",
  "Drone Authentication",
  "Drone Video Ingress",
  "Admin",
  "Fleet",
  "Drones",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getHealthEndpoint: build.query<
        GetHealthEndpointApiResponse,
        GetHealthEndpointApiArg
      >({
        query: () => ({ url: `/api/health` }),
        providesTags: ["System"],
      }),
      postAuthEndpoint: build.mutation<
        PostAuthEndpointApiResponse,
        PostAuthEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drone/auth`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Drone Authentication"],
      }),
      postCreateDroneVideoStreamEndpoint: build.mutation<
        PostCreateDroneVideoStreamEndpointApiResponse,
        PostCreateDroneVideoStreamEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drone/video/streams`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Drone Video Ingress"],
      }),
      getListDroneVideoStreamsEndpoint: build.query<
        GetListDroneVideoStreamsEndpointApiResponse,
        GetListDroneVideoStreamsEndpointApiArg
      >({
        query: () => ({ url: `/api/drone/video/streams` }),
        providesTags: ["Drone Video Ingress"],
      }),
      deleteDeleteDroneVideoStreamEndpoint: build.mutation<
        DeleteDeleteDroneVideoStreamEndpointApiResponse,
        DeleteDeleteDroneVideoStreamEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drone/video/streams/${queryArg.name}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Drone Video Ingress"],
      }),
      getQueueEndpoint: build.query<
        GetQueueEndpointApiResponse,
        GetQueueEndpointApiArg
      >({
        query: () => ({ url: `/api/admin/queue` }),
        providesTags: ["Admin"],
      }),
      getListDronesEndpoint: build.query<
        GetListDronesEndpointApiResponse,
        GetListDronesEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/drones`,
          params: {
            status: queryArg.status,
            limit: queryArg.limit,
          },
        }),
        providesTags: ["Admin"],
      }),
      postApproveEndpoint: build.mutation<
        PostApproveEndpointApiResponse,
        PostApproveEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/drones/${queryArg.droneId}/approve`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Admin"],
      }),
      postRevokeEndpoint: build.mutation<
        PostRevokeEndpointApiResponse,
        PostRevokeEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/drones/${queryArg.droneId}/revoke`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Admin"],
      }),
      deleteForgetEndpoint: build.mutation<
        DeleteForgetEndpointApiResponse,
        DeleteForgetEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/drones/${queryArg.droneId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Admin"],
      }),
      getFleetStatusEndpoint: build.query<
        GetFleetStatusEndpointApiResponse,
        GetFleetStatusEndpointApiArg
      >({
        query: () => ({ url: `/api/fleet/status` }),
        providesTags: ["Fleet"],
      }),
      getDroneStatusEndpoint: build.query<
        GetDroneStatusEndpointApiResponse,
        GetDroneStatusEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drones/${queryArg.droneId}/status`,
        }),
        providesTags: ["Drones"],
      }),
      getTelemetryEndpoint: build.query<
        GetTelemetryEndpointApiResponse,
        GetTelemetryEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drones/${queryArg.droneId}/telemetry`,
          params: {
            startTime: queryArg.startTime,
            endTime: queryArg.endTime,
            limit: queryArg.limit,
          },
        }),
        providesTags: ["Drones"],
      }),
      getListVideoStreamsEndpoint: build.query<
        GetListVideoStreamsEndpointApiResponse,
        GetListVideoStreamsEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drones/${queryArg.droneId}/video/streams`,
        }),
        providesTags: ["Drones"],
      }),
      getGetVideoStreamEndpoint: build.query<
        GetGetVideoStreamEndpointApiResponse,
        GetGetVideoStreamEndpointApiArg
      >({
        query: (queryArg) => ({
          url: `/api/drones/${queryArg.droneId}/video/streams/${queryArg.name}`,
        }),
        providesTags: ["Drones"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as droneApi };
export type GetHealthEndpointApiResponse =
  /** status 200 Service is healthy */ {
    status: "ok";
    /** Unix timestamp in milliseconds */
    timestamp: number;
  };
export type GetHealthEndpointApiArg = void;
export type PostAuthEndpointApiResponse =
  /** status 200 Authentication successful (approved drone) */
    | {
        success: true;
        droneId: string;
        status: "approved";
        /** Access token (valid for 24 hours) */
        token: string;
      }
    | /** status 202 Drone pending approval (no token issued) */ {
        success: true;
        droneId: string;
        status: "pending";
      };
export type PostAuthEndpointApiArg = {
  body: {
    /** Unique drone identifier (alphanumeric, dashes, underscores, max 64 chars) */
    droneId: string;
    /** Drone's public key for future mTLS */
    publicKey?: string;
    /** Drone metadata */
    metadata?: {
      model?: string;
      manufacturer?: string;
      firmwareVersion?: string;
      hardwareId?: string;
    };
  };
};
export type PostCreateDroneVideoStreamEndpointApiResponse =
  /** status 200 Stream already exists, returning existing stream info */
    | {
        name: string;
        streamId: string;
        publish:
          | {
              webrtc: {
                whipUrl: string;
                bearerToken?: string;
              };
            }
          | {
              srt: {
                host: string;
                port: number;
                streamId: string;
              };
            }
          | {
              udprtp: {
                host: string;
                port: number;
              };
            };
        playbackUri: string;
      }
    | /** status 201 Stream created successfully */ {
        name: string;
        streamId: string;
        publish:
          | {
              webrtc: {
                whipUrl: string;
                bearerToken?: string;
              };
            }
          | {
              srt: {
                host: string;
                port: number;
                streamId: string;
              };
            }
          | {
              udprtp: {
                host: string;
                port: number;
              };
            };
        playbackUri: string;
      };
export type PostCreateDroneVideoStreamEndpointApiArg = {
  body: {
    /** Unique stream name (e.g., 'front-camera', 'thermal') */
    name: string;
  };
};
export type GetListDroneVideoStreamsEndpointApiResponse =
  /** status 200 List of streams */ {
    streams: {
      name: string;
      streamId: string;
      playbackUri: string;
    }[];
  };
export type GetListDroneVideoStreamsEndpointApiArg = void;
export type DeleteDeleteDroneVideoStreamEndpointApiResponse =
  /** status 200 Stream deleted successfully */ {
    success: boolean;
  };
export type DeleteDeleteDroneVideoStreamEndpointApiArg = {
  /** Stream name to delete */
  name: string;
};
export type GetQueueEndpointApiResponse = /** status 200 Queue retrieved */ {
  drones: {
    droneId: string;
    status: "pending";
    metadata: {
      [key: string]: any;
    };
    createdAt: number;
  }[];
  count: number;
};
export type GetQueueEndpointApiArg = void;
export type GetListDronesEndpointApiResponse = /** status 200 Drones list */ {
  drones: {
    droneId: string;
    status: "pending" | "approved" | "revoked";
    metadata: {
      [key: string]: any;
    };
    createdAt: number;
    updatedAt: number;
    approvedAt?: number;
    approvedBy?: string;
    revokedAt?: number;
    revokedBy?: string;
    revokeReason?: string;
  }[];
  count: number;
};
export type GetListDronesEndpointApiArg = {
  /** Filter by status */
  status?: "pending" | "approved" | "revoked";
  /** Maximum drones to return */
  limit?: number;
};
export type PostApproveEndpointApiResponse = /** status 200 Drone approved */ {
  success: true;
  droneId: string;
  status: "approved";
  /** New access token with full permissions */
  token: string;
  refreshToken: string;
};
export type PostApproveEndpointApiArg = {
  droneId: string;
  body: {
    /** Operator identifier */
    approvedBy: string;
  };
};
export type PostRevokeEndpointApiResponse = /** status 200 Drone revoked */ {
  success: true;
  droneId: string;
  status: "revoked";
};
export type PostRevokeEndpointApiArg = {
  droneId: string;
  body: {
    /** Operator identifier */
    revokedBy: string;
    /** Reason for revocation */
    reason?: string;
  };
};
export type DeleteForgetEndpointApiResponse =
  /** status 200 Drone forgotten */ {
    success: true;
    droneId: string;
  };
export type DeleteForgetEndpointApiArg = {
  droneId: string;
};
export type GetFleetStatusEndpointApiResponse = /** status 200 Fleet status */ {
  /** Total number of drones known to the fleet */
  totalDrones: number;
  /** Number of drones currently connected */
  connectedDrones: number;
};
export type GetFleetStatusEndpointApiArg = void;
export type GetDroneStatusEndpointApiResponse = /** status 200 Drone status */ {
  connected: boolean;
  drone: {
    droneId: string;
    model: string;
    manufacturer: string;
    firmwareVersion: string;
    hardwareId: string;
    updatedAt: number;
  } | null;
  telemetry: {
    /** Unix timestamp in milliseconds */
    timestamp: number;
    /** Drone identifier */
    droneId: string;
    position?: {
      latitude: number;
      longitude: number;
      altitude: number;
    };
    attitude?: {
      roll: number;
      pitch: number;
      yaw: number;
    };
    velocity?: {
      north: number;
      east: number;
      down: number;
    };
    battery?: {
      voltage: number;
      percentage: number;
    };
    signal?: {
      available: boolean;
      strength: number;
    };
  } | null;
};
export type GetDroneStatusEndpointApiArg = {
  /** Drone ID */
  droneId: string;
};
export type GetTelemetryEndpointApiResponse = /** status 200 Telemetry data */ {
  data: {
    /** Unix timestamp in milliseconds */
    timestamp: number;
    /** Drone identifier */
    droneId: string;
    position?: {
      latitude: number;
      longitude: number;
      altitude: number;
    };
    attitude?: {
      roll: number;
      pitch: number;
      yaw: number;
    };
    battery?: {
      voltage: number;
      percentage: number;
    };
    signal?: {
      available: boolean;
      strength: number;
    };
    custom?: {
      [key: string]: any;
    };
  }[];
};
export type GetTelemetryEndpointApiArg = {
  /** Drone ID to query telemetry for */
  droneId: string;
  /** Start timestamp filter */
  startTime?: number | null;
  /** End timestamp filter */
  endTime?: number | null;
  /** Max results to return */
  limit?: number;
};
export type GetListVideoStreamsEndpointApiResponse =
  /** status 200 List of streams */ {
    streams: {
      name: string;
      streamId: string;
      playbackUri: string;
    }[];
  };
export type GetListVideoStreamsEndpointApiArg = {
  /** Drone ID */
  droneId: string;
};
export type GetGetVideoStreamEndpointApiResponse =
  /** status 200 Stream info */ {
    name: string;
    streamId: string;
    playbackUri: string;
  };
export type GetGetVideoStreamEndpointApiArg = {
  /** Drone ID */
  droneId: string;
  /** Stream name */
  name: string;
};
export const {
  useGetHealthEndpointQuery,
  usePostAuthEndpointMutation,
  usePostCreateDroneVideoStreamEndpointMutation,
  useGetListDroneVideoStreamsEndpointQuery,
  useDeleteDeleteDroneVideoStreamEndpointMutation,
  useGetQueueEndpointQuery,
  useGetListDronesEndpointQuery,
  usePostApproveEndpointMutation,
  usePostRevokeEndpointMutation,
  useDeleteForgetEndpointMutation,
  useGetFleetStatusEndpointQuery,
  useGetDroneStatusEndpointQuery,
  useGetTelemetryEndpointQuery,
  useGetListVideoStreamsEndpointQuery,
  useGetGetVideoStreamEndpointQuery,
} = injectedRtkApi;
